import React, { useCallback, useEffect, useState } from 'react';
import { Check, Fingerprint, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import {
  BIOMETRIC_ACTIONS, biometricErrorMessage, biometricSupport,
  enrollBiometricDevice, listBiometricDevices, revokeBiometricDevice,
} from '../lib/biometric';
import { toast } from '../lib/buses';

// "Which devices can authorise as me?" — in the operator's own menu, where an identity
// question belongs, rather than buried in Utilities behind a tab called Biometric
// Enrollment that described itself as POS hardware setup.
//
// The credentials are keyed by USER id (server-side, from the session — never from the
// request body), so this list follows the person across browsers and machines. It is not a
// property of this browser.

// WebAuthn deliberately gives a site no way to enumerate what is on the machine, so
// "is THIS the device I'm sitting at?" cannot be asked of the platform. The one honest
// answer available is a local note of what this browser enrolled — hence a localStorage
// marker, and hence the wording "enrolled from this browser" rather than a claim about
// hardware we cannot actually check.
const THIS_DEVICE_KEY = 'tkt:biometric:thisDevice';
const readThisDevice = () => { try { return window.localStorage.getItem(THIS_DEVICE_KEY); } catch { return null; } };
const writeThisDevice = (id) => { try { window.localStorage.setItem(THIS_DEVICE_KEY, id); } catch { /* private mode */ } };

const fmt = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const defaultDeviceName = () => {
  const ua = navigator.userAgent || '';
  const os = /Mac/i.test(ua) ? 'Mac' : /Windows/i.test(ua) ? 'Windows PC' : /Android/i.test(ua) ? 'Android' : /iPhone|iPad/i.test(ua) ? 'iPhone' : 'This device';
  const browser = /Edg\//i.test(ua) ? 'Edge' : /Chrome\//i.test(ua) ? 'Chrome' : /Safari\//i.test(ua) ? 'Safari' : /Firefox\//i.test(ua) ? 'Firefox' : '';
  return browser ? `${os} · ${browser}` : os;
};

export const BiometricPanel = ({ action = BIOMETRIC_ACTIONS.confirmOrder }) => {
  const [support, setSupport] = useState(null);
  const [devices, setDevices] = useState({ status: 'loading', rows: [] });
  const [enrolling, setEnrolling] = useState(false);
  const [name, setName] = useState('');
  const [busyId, setBusyId] = useState(null);
  const thisDevice = readThisDevice();

  const load = useCallback(() => {
    listBiometricDevices()
      .then((rows) => setDevices({ status: 'ready', rows }))
      .catch(() => setDevices({ status: 'error', rows: [] }));
  }, []);

  useEffect(() => { biometricSupport().then(setSupport); load(); }, [load]);

  const doEnroll = async () => {
    setEnrolling(true);
    try {
      const r = await enrollBiometricDevice({ deviceName: name.trim() || defaultDeviceName(), action });
      writeThisDevice(r.credentialId);
      setName('');
      toast('Device enrolled', 'success', `${r.deviceName} can now authorise as you`);
      load();
    } catch (e) {
      toast('Could not enrol', 'error', biometricErrorMessage(e));
    } finally { setEnrolling(false); }
  };

  const doRevoke = async (d) => {
    setBusyId(d.id);
    try {
      await revokeBiometricDevice(d.id);
      toast('Device removed', 'warning', `${d.deviceName} can no longer authorise`);
      load();
    } catch (e) {
      toast('Could not remove device', 'error', e?.message);
    } finally { setBusyId(null); }
  };

  const recognised = devices.rows.some((d) => d.id === thisDevice);

  return (
    <div className="px-4 py-3 border-b border-[#EEEDEA]">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-semibold tracking-[0.06em] uppercase text-[#9CA3AF]">Biometric authorisation</div>
        {recognised && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-[#1F6A3A]" title="This browser enrolled one of the devices below — it can authorise without another step.">
            <ShieldCheck className="w-3 h-3" />Recognised
          </span>
        )}
      </div>

      {devices.status === 'loading' && <div className="py-3 text-center"><Loader2 className="w-3.5 h-3.5 text-[#9CA3AF] animate-spin inline-block" /></div>}
      {devices.status === 'error' && <div className="text-[11px] text-[#9B1F1F] py-1.5">Couldn't load your devices.</div>}

      {devices.status === 'ready' && devices.rows.length > 0 && (
        <div className="space-y-1 mb-2">
          {devices.rows.map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-md border border-[#EEEDEA] px-2.5 py-1.5">
              <Fingerprint className="w-3.5 h-3.5 text-[#6B7280] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-[#1F2937] truncate">
                  {d.deviceName}
                  {d.id === thisDevice && <span className="text-[10px] text-[#1F6A3A] ml-1.5">· this browser</span>}
                </div>
                <div className="text-[10px] text-[#9CA3AF]">
                  {d.action}{fmt(d.createdAt) ? ` · added ${fmt(d.createdAt)}` : ''}{d.lastUsed ? ` · last used ${fmt(d.lastUsed)}` : ' · never used'}
                </div>
              </div>
              <button
                onClick={() => doRevoke(d)}
                disabled={busyId === d.id}
                title={`Remove ${d.deviceName} — it will no longer be able to authorise actions as you.`}
                className="text-[#9CA3AF] hover:text-[#9B1F1F] flex-shrink-0 disabled:opacity-40"
              >
                {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}

      {devices.status === 'ready' && devices.rows.length === 0 && (
        <div className="text-[11px] text-[#6B7280] leading-relaxed mb-2">
          No device enrolled. Enrol one and confirming an order will ask for your fingerprint or face instead of an emailed code.
        </div>
      )}

      {support && !support.available ? (
        <div className="text-[10.5px] text-[#9CA3AF] leading-relaxed">{support.reason}</div>
      ) : (
        <div className="flex items-center gap-1.5">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultDeviceName()}
            onKeyDown={(e) => { if (e.key === 'Enter') doEnroll(); }}
            className="flex-1 h-7 px-2 text-[11.5px] bg-white border border-[#E6E4E1] rounded-md focus:outline-none focus:border-[#1F2937]"
          />
          <button
            onClick={doEnroll}
            disabled={enrolling}
            title="Registers this device against your account. The fingerprint itself never leaves the device — only a public key is stored."
            className="h-7 px-2.5 rounded-md text-[11.5px] font-medium bg-[#1F2937] hover:bg-[#111827] text-white inline-flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
          >
            {enrolling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Enrol
          </button>
        </div>
      )}
    </div>
  );
};
